import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: ['micro', 'detail', 'table'] }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a search volume number into compact form (e.g. 2100 → "2.1k", 1500000 → "1.5M").
 * Returns null if the input is null, undefined, or zero.
 */
export function formatVolume(volume: number | null | undefined): string | null {
  if (volume == null || volume === 0) return null;
  if (volume >= 1_000_000) return `${(volume / 1_000_000).toFixed(1)}M`;
  if (volume >= 1_000) return `${(volume / 1_000).toFixed(1)}k`;
  return String(volume);
}

// Prompt category constants, tag helpers, engine labels, coverage tier labels,
// competitive outcome labels, and engine list — single source of truth.
export {
  PROMPT_CATEGORIES,
  EXTRACTOR_CATEGORIES,
  CATEGORY_LABELS,
  PROMPT_INTENTS,
  INTENT_LABELS,
  intentLabel,
  buildPromptTags,
  ENGINE_LABELS,
  ENGINES,
  COVERAGE_ORDER,
  COVERAGE_LABELS,
  OUTCOME_LABELS,
  EFFECTIVENESS_LABEL,
  EFFECTIVENESS_HINT,
} from './promptTags';
export type {
  PromptCategory,
  PromptIntent,
  PromptTagKind,
  PromptTag,
  CoverageTier,
  CompetitiveOutcome,
} from './promptTags';

// SERP feature labels — maps serp_item_types to human-readable labels.
// Only surfaces noteworthy features; common ones (organic, related_searches) are skipped.
export const SERP_FEATURE_LABELS: Record<string, string> = {
  people_also_ask: 'PAA',
  featured_snippet: 'Featured Snippet',
  knowledge_panel: 'Knowledge Panel',
  local_pack: 'Local Pack',
  top_stories: 'Top Stories',
  image_pack: 'Images',
  video: 'Video',
  shopping: 'Shopping',
  ai_overview: 'AI Overview',
};

export function formatSerpFeatures(raw: string[] | null | undefined): string[] {
  if (!raw || raw.length === 0) return [];
  return raw.map((f) => SERP_FEATURE_LABELS[f])
    .filter((label): label is string => !!label);
}

// THE canonical issue_type → label map (rule 25: one domain-label map, one place).
// Used by the console /issues catalog AND the shared IssuesDataTable (report body +
// report Evidence), so the same lane reads identically everywhere.
//
// Every label is the FULL domain word. A label is NEVER pre-abbreviated to fit a
// column: `DataTable` truncates cells on its own, and a trailing period in a cell
// or chip label is simply wrong typographically. When a label is too wide for its
// track, the fix is the TRACK (visual review 2026-07-25 — this replaced the earlier
// "kept concise enough to sit in a table cell" rule, under which
// `competitor_recommended` was the only abbreviation in the map).
export const ISSUE_TYPE_LABELS: Record<string, string> = {
  visibility_gap: 'Visibility gap',
  feature_loss: 'Capability loss',
  misrepresentation: 'Misrepresentation',
  reputation_concern: 'Reputation concern',
  competitor_recommended: 'Competitor recommended',
  negative_sentiment: 'Negative sentiment',
  source_leak: 'Source leak',
  brand_misattribution: 'Brand misattribution',
  source_imbalance: 'Source imbalance',
  site_health: 'Site health',
  page_blocked: 'Page blocked',
  page_geo_health: 'Page GEO health',
  content_decay: 'Content decay',
  cited_but_silent: 'Cited but silent',
  content_mix: 'Content gap',
  persona_coverage_gap: 'Buyer-role gap',
  theme_coverage_gap: 'Theme gap',
  dimension_gap: 'Dimension gap',
  archetype_coverage_gap: 'Page-type gap',
  uncovered_demand: 'Uncovered demand',
  vocabulary_gap: 'Vocabulary gap',
  rank_when_listed: 'Low ranking',
  dismissive_answer: 'Dismissive answer',
};

/** Human label for an issue_type — the canonical map above, falling back to a
 *  title-cased slug for any lane not yet named. */
export function issueTypeLabel(raw: string): string {
  return (
    ISSUE_TYPE_LABELS[raw] ??
    raw
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

// Date/time/duration formatting (consolidated from the console's date utils)
export * from './dateFormat';

// Byte sizes, transfer rates and time-remaining phrasing (THE register — the
// same relationship to a file size that `dateFormat` has to a date)
export { formatBytes, formatTransferRate, formatTimeRemaining } from './byteFormat';

// Dollar amounts and token counts (THE money register — same relationship to a
// currency figure that `dateFormat` has to a date)
export { formatUsd, formatTokens } from './moneyFormat';

// Operation-aware labels shared by console and public playbook renderers.
export { pagePatchLabel, pagePatchPresentation } from './pagePatchPresentation';
export type {
  PagePatchLocatorKind,
  PagePatchOperation,
  PagePatchPresentation,
} from './pagePatchPresentation';

// Avatar color utilities
export * from './avatarColors';

// Cache utilities for TTL-based caching and request deduplication
export * from './cache';

// Citation badge preprocessing for markdown responses
export * from './citationPreprocess';

// Insights-to-findings conversion for eval detail annotation system
export { insightsToFindings, sortFindingsByPosition, deconflictFindings, anchorAnnotations, semanticDedup } from './insightsToFindings';
export type { InsightsToFindingsInput } from './insightsToFindings';

export { buildEvalFindings, misrepsToAnnotations } from './buildEvalFindings';

export { claimsToAnnotations } from './claimsToAnnotations';
export type { ClaimTraceRow, ClaimSource } from './claimsToAnnotations';

// Source ownership classification utilities (DRY — single source of truth)
export {
  SOURCE_OWNER_KIND,
  isOwnedSource,
  sourceRoleFromOwnerType,
  ownerBucketFromType,
  ownerSplitWeighted,
  formatOwnerType,
  ownerTypeColor,
  ownerTypeBorderColor,
  ownerDisplayLabel,
  competitorIdentityIndex,
  ownerRevealClass,
  OWNER_TYPE_COLORS,
  SOURCE_TYPE_COLUMN_LABEL,
  CONTENT_TYPE_COLUMN_LABEL,
  formatSourceType,
  sourceTypeColor,
  sourceTypeBorderColor,
  sharedHostBucket,
} from './sourceOwnership';
export type { SourceOwnerKind, SourceRole, OwnerBucket, CompetitorIdentity } from './sourceOwnership';

// URL display helpers — single source of truth for path-from-URL extraction
export { pagePath, sourceDisplayUrl, soleHost } from './urlFormat';

// URL normalization + hashing — client-side mirror of backend url_identity.py
export { normalizeUrl, urlHash } from './urlHash';

// Status → semantic color mapping — single source for all apps.
export { getStatusClasses, normalizeStatus } from './statusNormalization';
export type { StatusCategory, StatusClasses } from './statusNormalization';

// Page-access label — WHY a text-derived signal is blank ("Blocked"/"Media").
// ONE vocabulary for every surface that shows signals derived from page text.
export { pageAccessLabel } from './pageAccess';
export type { PageAccessLabel } from './pageAccess';

// Company-domain normalization + name derivation — single source of truth.
export { isValidDomain, normalizeDomain, nameFromDomain } from './domain';

// Weekly benchmark-schedule cron helpers — UTC storage, local-time display.
export {
  buildCron,
  localPartsFromCron,
  cronSummary,
  nextRunFromCron,
  nextRunLabel,
  DAYS_OF_WEEK,
  HOURS,
  DEFAULT_CRON,
} from './cronUtils';
